import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, FolderPlus } from 'lucide-react';
import { useId, useState, type ReactNode, type RefObject } from 'react';
import { TASK_PROJECT_DESCRIPTION_MAX_LENGTH, TASK_PROJECT_NAME_MAX_LENGTH, TASK_PROJECT_TEXTS } from '@till-erp/collab/values';
import { apiFetch, ApiProblem, newIdempotencyKey, type TaskProject, type TaskProjectList } from '../lib/api.ts';
import { formatDate, isoDateInBerlin } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import { Sheet, SHEET_ACTIONS } from './sheet.tsx';
import type { UndoOffer } from './undo-toast.tsx';
import { Button } from './ui/button.tsx';
import { CapsuleBar } from './ui/capsule-bar.tsx';
import { Combobox } from './ui/combobox.tsx';
import { DateField } from './ui/date-field.tsx';
import { Input } from './ui/input.tsx';
import { ProgressRing, ringPercent } from './ui/progress-ring.tsx';
import { Textarea } from './ui/textarea.tsx';
import { pillToneClass } from './ui/pill-tone.ts';

/**
 * Projekte der Aufgaben (Story 23.7, FR-160; Entwurf A `ux-till-erp/entwuerfe-2026-10-30-aufgaben-projekte/`, Freigabe
 * HS54 mit Auflagen): Karten-Galerie wie Linear „Projects“ / Notion-Galerie und Projektseite mit Fortschritts-Ring.
 * Fortschritt rechnet nur der Server beim Lesen (erledigt/gesamt, Papierkorb zählt nie); hier wird nichts gezählt.
 * Ein Signal je Information: Fortschritt als Balken + „7 von 12 erledigt“, Überfälligkeit nur am roten Ziel-Chip,
 * Personen als Kürzel-Stapel. 100 % → grüner Balken und kleiner Knopf „Abschließen“ (ohne Rückfrage, 10 s Rückgängig).
 */

export const TASK_PROJECT_UI_TEXTS = {
  tab: 'Projekte',
  newProject: 'Neues Projekt',
  emptyHint: 'Name genügt – Ziel-Datum später',
  search: 'Projekt suchen …',
  searchLabel: 'Projekte durchsuchen',
  state: 'Zustand der Projekte',
  active: 'Aktiv',
  closedState: 'Abgeschlossen',
  close: 'Abschließen',
  closeLabel: (name: string) => `„${name}“ abschließen`,
  reopen: 'Wieder öffnen',
  edit: 'Bearbeiten',
  addTask: 'Aufgabe',
  back: 'Alle Projekte',
  target: (date: string) => `Ziel ${formatDate(date)}`,
  closedOn: (date: string) => `Abgeschlossen ${formatDate(date)}`,
  closedToast: (name: string, open: number) =>
    open === 0 ? `„${name}“ abgeschlossen` : `„${name}“ abgeschlossen · ${open === 1 ? '1 Aufgabe bleibt' : `${open} Aufgaben bleiben`} offen`,
  reopened: (name: string) => `„${name}“ wieder offen`,
  created: (name: string) => `Projekt „${name}“ angelegt`,
  saved: (name: string) => `Projekt „${name}“ gespeichert`,
  noHits: 'Kein Projekt passt zur Suche.',
  noActive: 'Kein aktives Projekt. Alle Projekte sind abgeschlossen.',
  noClosed: 'Noch kein Projekt abgeschlossen.',
  people: (names: string[]) => `Beteiligt: ${names.join(', ')}`,
  ring: 'Fortschritt des Projekts',
  ringText: (done: number, total: number) => (total === 0 ? TASK_PROJECT_TEXTS.noTasks : TASK_PROJECT_TEXTS.progress(done, total)),
  dialogNew: 'Neues Projekt',
  dialogEdit: 'Projekt bearbeiten',
  name: 'Name',
  namePlaceholder: 'z. B. Katalog 2027',
  targetDate: 'Ziel-Datum',
  targetClear: 'Ziel-Datum entfernen',
  description: 'Beschreibung',
  descriptionPlaceholder: 'Worum geht es? Was gehört dazu?',
  create: 'Anlegen',
  save: 'Speichern',
  project: 'Projekt',
  projectSet: (name: string) => `Projekt „${name}“ zugeordnet`,
  projectCleared: 'Aus dem Projekt genommen',
  list: 'Liste',
  board: 'Board',
  view: 'Ansicht des Projekts',
  inProject: (name: string) => `Projekt „${name}“`,
  staleLoaded: (p: Pick<TaskProject, 'name' | 'targetDate'>) =>
    `Jemand hat das Projekt inzwischen geändert – jetzt „${p.name}“${p.targetDate ? `, Ziel ${formatDate(p.targetDate)}` : ', ohne Ziel-Datum'}. Ihre Eingaben sind noch da; „Speichern“ übernimmt sie.`,
  staleTake: 'Neuen Stand übernehmen',
} as const;

/** Auffrischen der Projekt-Werte wie Board und Kalender (Aufgaben anderer ändern den Fortschritt). */
export const TASK_PROJECTS_REFRESH_MS = 30_000;

export const taskProjectsKey = ['tasks', 'projects'] as const;
export const taskProjectKey = (id: string) => ['tasks', 'projects', 'one', id] as const;

/** Alle Projekte (aktive und abgeschlossene) in der Reihenfolge des Servers. */
export function useTaskProjects(enabled = true) {
  const { getToken } = useSession();
  return useQuery({ queryKey: taskProjectsKey, queryFn: () => apiFetch<TaskProjectList>(getToken, '/api/v1/tasks/projects'), enabled, refetchInterval: TASK_PROJECTS_REFRESH_MS });
}

/** Ein Projekt mit Fortschritt (Projektseite). */
export function useTaskProject(id: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: taskProjectKey(id ?? ''),
    queryFn: () => apiFetch<TaskProject>(getToken, `/api/v1/tasks/projects/${id}`),
    enabled: id !== null,
    refetchInterval: TASK_PROJECTS_REFRESH_MS,
  });
}

function problemText(err: unknown): string {
  return err instanceof ApiProblem ? (Object.values(err.errors)[0] ?? err.detail) : 'Etwas ist schiefgelaufen.';
}

/** Alles erledigt (und mindestens eine Aufgabe): grüner Balken und „Abschließen“. */
export function projectComplete(p: Pick<TaskProject, 'done' | 'total'>): boolean {
  return p.total > 0 && p.done === p.total;
}

/** Suche über Name und Beschreibung (ohne Groß/Klein). */
export function matchesProject(p: Pick<TaskProject, 'name' | 'description'>, query: string): boolean {
  const q = query.trim().toLocaleLowerCase('de');
  if (q === '') return true;
  return p.name.toLocaleLowerCase('de').includes(q) || (p.description ?? '').toLocaleLowerCase('de').includes(q);
}

const CHIP = 'inline-flex min-h-7 max-w-full items-center rounded-full px-space-3 type-meta whitespace-nowrap';

/**
 * Ziel-Chip: „Ziel 15.03.2027“, überfällig rot mit „· 3 Tage überfällig“, ohne Datum „Ohne Ziel-Datum“. `compact` (Karte
 * der Galerie): überfällig nur „3 Tage überfällig“, das Datum im Tooltip – so passt der Chip neben die Personen.
 */
export function ProjectTargetChip({ project, compact = false, className }: { project: TaskProject; compact?: boolean; className?: string }) {
  if (project.state === 'closed' && project.closedAt) {
    return (
      <span data-testid="projekt-ziel" className={cn(CHIP, 'bg-tile text-ink-2', className)}>
        {TASK_PROJECT_UI_TEXTS.closedOn(isoDateInBerlin(new Date(project.closedAt)))}
      </span>
    );
  }
  if (!project.targetDate) {
    return (
      <span data-testid="projekt-ziel" className={cn(CHIP, 'bg-tile text-ink-3', className)}>
        {TASK_PROJECT_TEXTS.noTarget}
      </span>
    );
  }
  return (
    <span
      data-testid="projekt-ziel"
      data-overdue={project.overdue ? '' : undefined}
      title={compact && project.overdue ? TASK_PROJECT_UI_TEXTS.target(project.targetDate) : undefined}
      className={cn(CHIP, project.overdue ? 'bg-clarify-bg font-medium text-clarify' : 'bg-tile text-ink-2', className)}
    >
      <span className="truncate">
        {compact && project.overdue
          ? TASK_PROJECT_TEXTS.overdue(project.overdueDays)
          : `${TASK_PROJECT_UI_TEXTS.target(project.targetDate)}${project.overdue ? ` · ${TASK_PROJECT_TEXTS.overdue(project.overdueDays)}` : ''}`}
      </span>
    </span>
  );
}

/** Kürzel-Stapel der Beteiligten (höchstens drei, dann „+n“); die Namen liest der Hilfsmittel-Name vor. */
export function PeopleStack({ people, className }: { people: TaskProject['people']; className?: string }) {
  if (people.length === 0) return null;
  const shown = people.slice(0, 3);
  const more = people.length - shown.length;
  return (
    <span role="img" aria-label={TASK_PROJECT_UI_TEXTS.people(people.map((p) => p.name))} data-testid="projekt-personen" className={cn('flex flex-none items-center', className)}>
      {shown.map((p, i) => (
        <span
          key={p.id}
          aria-hidden
          title={p.name}
          className={cn('grid size-7 place-items-center rounded-full bg-brand-tile text-micro font-medium text-brand-tile-fg ring-2 ring-card', i > 0 && '-ml-space-2')}
        >
          {p.initials}
        </span>
      ))}
      {more > 0 ? (
        <span aria-hidden className="-ml-space-2 grid size-7 place-items-center rounded-full bg-tile text-micro font-medium text-ink-2 ring-2 ring-card">
          +{more}
        </span>
      ) : null}
    </span>
  );
}

/** Balken und Satz „7 von 12 erledigt“; 100 % grün. */
function ProjectProgress({ project, action }: { project: TaskProject; action?: ReactNode }) {
  const complete = projectComplete(project);
  const text = TASK_PROJECT_UI_TEXTS.ringText(project.done, project.total);
  return (
    <div className="flex flex-col gap-space-2">
      <CapsuleBar
        value={project.total === 0 ? 0 : project.done / project.total}
        tone={complete ? 'ready' : 'accent'}
        label={`Fortschritt ${project.name}`}
        valueText={text}
      />
      <span className="flex min-h-8 items-center justify-between gap-space-2">
        <span data-testid="projekt-fortschritt" className="text-body text-ink-2 tabular-nums">
          {text}
        </span>
        {action}
      </span>
    </div>
  );
}

/**
 * Karte der Galerie: ganze Karte = Link zur Projektseite (ein Klickziel); „Abschließen“ liegt darüber, nur bei 100 % und
 * aktivem Projekt.
 */
function ProjectCard({ project, busy, onClose }: { project: TaskProject; busy: boolean; onClose: (p: TaskProject) => void }) {
  const complete = projectComplete(project) && project.state === 'active';
  return (
    <li
      data-testid="projekt-karte"
      data-project-id={project.id}
      className="group relative flex min-w-0 flex-col gap-space-4 rounded-card bg-surface-card p-space-5 transition-[background-color,transform] duration-(--motion-fast) ease-(--motion-ease) hover:-translate-y-px hover:bg-hover focus-within:bg-hover"
    >
      <div className="flex min-w-0 flex-col gap-space-1">
        <h3 className="m-0 min-w-0 text-tile-title font-medium text-ink">
          <Link
            to="/aufgaben/projekte/$projectId"
            params={{ projectId: project.id }}
            className="block truncate after:absolute after:inset-0 after:rounded-tile after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
          >
            {project.name}
          </Link>
        </h3>
        {project.description ? <p className="m-0 truncate text-body text-ink-2">{project.description}</p> : null}
      </div>
      <ProjectProgress
        project={project}
        action={
          complete ? (
            <button
              type="button"
              disabled={busy}
              aria-label={TASK_PROJECT_UI_TEXTS.closeLabel(project.name)}
              onClick={() => onClose(project)}
              data-testid="projekt-abschliessen"
              className={cn('relative z-10 inline-flex min-h-8 cursor-pointer items-center rounded-full px-space-3 type-meta font-medium hover:underline disabled:cursor-default disabled:opacity-50 pointer-coarse:min-h-11', pillToneClass('ready'))}
            >
              {TASK_PROJECT_UI_TEXTS.close}
            </button>
          ) : null
        }
      />
      <div className="mt-auto flex min-w-0 items-center justify-between gap-space-2">
        <ProjectTargetChip project={project} compact className="min-w-0" />
        <PeopleStack people={project.people} />
      </div>
    </li>
  );
}

/** Gestrichelte Leer-Karte (nur wenn es noch kein Projekt gibt, Auflage 1). */
function EmptyProjectCard({ onNew }: { onNew: () => void }) {
  return (
    <button
      type="button"
      onClick={onNew}
      data-testid="projekt-leer-karte"
      className="flex min-h-44 w-full cursor-pointer flex-col items-center justify-center gap-space-1 rounded-tile border border-dashed border-ring-line bg-transparent p-space-5 text-center hover:bg-tile focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none tablet:max-w-sm"
    >
      <span className="inline-flex items-center gap-space-2 text-row font-medium text-link">
        <FolderPlus size={16} strokeWidth={1.75} aria-hidden />＋ {TASK_PROJECT_UI_TEXTS.newProject}
      </span>
      <span className="text-body text-ink-3">{TASK_PROJECT_UI_TEXTS.emptyHint}</span>
    </button>
  );
}

export type ProjectStateFilter = 'active' | 'closed';

/** Segment-Optionen „Aktiv n · Abgeschlossen n“ (Zähler aus der Liste). */
export function projectStateOptions(items: readonly TaskProject[]) {
  return [
    { value: 'active' as const, label: TASK_PROJECT_UI_TEXTS.active, count: items.filter((p) => p.state === 'active').length },
    { value: 'closed' as const, label: TASK_PROJECT_UI_TEXTS.closedState, count: items.filter((p) => p.state === 'closed').length },
  ];
}

/** Galerie im Reiter „Projekte“: Reihenfolge kommt vom Server (überfällig, Ziel-Datum, Name). */
export function ProjectGallery({
  items,
  state,
  search,
  busy,
  onNew,
  onClose,
}: {
  items: readonly TaskProject[];
  state: ProjectStateFilter;
  search: string;
  busy: boolean;
  onNew: () => void;
  onClose: (p: TaskProject) => void;
}) {
  if (items.length === 0) {
    return (
      <section aria-label={TASK_PROJECT_UI_TEXTS.tab} className="card-box mb-card-gap" data-testid="projekte-galerie">
        <EmptyProjectCard onNew={onNew} />
      </section>
    );
  }
  const inState = items.filter((p) => p.state === state);
  const shown = inState.filter((p) => matchesProject(p, search));
  return (
    <section aria-label={TASK_PROJECT_UI_TEXTS.tab} className="mb-card-gap animate-fade-in" data-testid="projekte-galerie">
      {shown.length === 0 ? (
        <p className="m-0 text-body text-ink-3" data-testid="projekte-keine">
          {inState.length === 0 ? (state === 'active' ? TASK_PROJECT_UI_TEXTS.noActive : TASK_PROJECT_UI_TEXTS.noClosed) : TASK_PROJECT_UI_TEXTS.noHits}
        </p>
      ) : (
        <ul className="m-0 grid list-none grid-cols-1 gap-card-gap p-0 tablet:grid-cols-2 desktop:grid-cols-4">
          {shown.map((p) => (
            <ProjectCard key={p.id} project={p} busy={busy} onClose={onClose} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Kopf der Projektseite: Fortschritts-Ring „7/12“ (Signatur-Element, die einzige Fortschrittsangabe hier), Name, Ziel-Chip,
 * Beschreibung, Kürzel; Knöpfe Bearbeiten · Abschließen (bzw. Wieder öffnen) · „+ Aufgabe“.
 */
export function ProjectHeader({
  project,
  busy,
  editOpener,
  onEdit,
  onClose,
  onReopen,
  onAddTask,
}: {
  project: TaskProject;
  busy: boolean;
  editOpener: RefObject<HTMLButtonElement | null>;
  onEdit: () => void;
  onClose: () => void;
  onReopen: () => void;
  onAddTask: () => void;
}) {
  const complete = projectComplete(project);
  const ringText = TASK_PROJECT_UI_TEXTS.ringText(project.done, project.total);
  return (
    <section aria-label={project.name} data-testid="projekt-kopf" className="card-box mb-card-gap flex flex-wrap items-center gap-x-space-5 gap-y-space-4">
      <ProgressRing
        testId="projekt-ring"
        value={project.total === 0 ? 0 : project.done / project.total}
        label={TASK_PROJECT_UI_TEXTS.ring}
        meter={{ valueText: ringText, now: ringPercent(project.done, project.total) ?? 0 }}
        size="md"
        className={cn(complete && '[&_[data-ring-fill]]:stroke-ready')}
      >
        {project.done}/{project.total}
      </ProgressRing>
      <div className="flex min-w-0 flex-[1_1_10rem] flex-col gap-space-1">
        <Link
          to="/aufgaben"
          search={{ ansicht: 'projekte' }}
          className="inline-flex min-h-8 items-center gap-space-1 self-start type-meta text-ink-3 hover:text-brand hover:underline pointer-coarse:min-h-11"
          data-testid="projekt-zurueck"
        >
          <ArrowLeft size={14} strokeWidth={1.75} aria-hidden />
          {TASK_PROJECT_UI_TEXTS.back}
        </Link>
        <h2 className="m-0 min-w-0 text-tile-title font-medium break-words text-ink" data-testid="projekt-name">
          {project.name}
        </h2>
        <div className="flex min-w-0 flex-wrap items-center gap-x-space-3 gap-y-space-2">
          <ProjectTargetChip project={project} />
          {project.description ? <span className="min-w-0 text-body break-words text-ink-2">{project.description}</span> : null}
          <PeopleStack people={project.people} />
        </div>
      </div>
      <div className="flex flex-wrap gap-space-2 max-tablet:w-full max-tablet:[&>button]:flex-1">
        <Button ref={editOpener} variant="secondary" onClick={onEdit} disabled={busy} data-testid="projekt-bearbeiten">
          {TASK_PROJECT_UI_TEXTS.edit}
        </Button>
        {project.state === 'active' ? (
          <Button variant="secondary" onClick={onClose} disabled={busy} data-testid="projekt-kopf-abschliessen">
            {TASK_PROJECT_UI_TEXTS.close}
          </Button>
        ) : (
          <Button variant="secondary" onClick={onReopen} disabled={busy} data-testid="projekt-wieder-oeffnen">
            {TASK_PROJECT_UI_TEXTS.reopen}
          </Button>
        )}
        {project.state === 'active' ? (
          <Button onClick={onAddTask} disabled={busy} data-testid="projekt-neue-aufgabe">
            ＋ {TASK_PROJECT_UI_TEXTS.addTask}
          </Button>
        ) : null}
      </div>
    </section>
  );
}

/** Projekt ohne Aufgaben: statt leerem Board / leerer Liste ein Leerzustand mit „+ Aufgabe“ (nur aktiv). */
export function ProjectNoTasks({ active, busy, onAddTask }: { active: boolean; busy: boolean; onAddTask: () => void }) {
  return (
    <section data-testid="projekt-ohne-aufgaben" className="card-box mb-card-gap flex flex-col items-center gap-space-3 py-space-6 text-center">
      <p className="m-0 text-row font-medium text-ink">{TASK_PROJECT_TEXTS.noTasks}</p>
      {active ? (
        <Button variant="secondary" onClick={onAddTask} disabled={busy} data-testid="projekt-leer-aufgabe">
          ＋ {TASK_PROJECT_UI_TEXTS.addTask}
        </Button>
      ) : null}
    </section>
  );
}

/** Blatt „Neues Projekt“ / „Projekt bearbeiten“: Name (Pflicht), Ziel-Datum, Beschreibung. */
export function ProjectDialog({
  project,
  today,
  opener,
  onClose,
  onSaved,
}: {
  /** Ohne Angabe: neues Projekt. */
  project?: TaskProject;
  today: string;
  opener: RefObject<HTMLElement | null>;
  onClose: () => void;
  onSaved: (saved: TaskProject, created: boolean) => void;
}) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  // Stand, gegen den gespeichert wird; nach 409 `task_project_stale` der frisch geladene (Eingaben bleiben stehen).
  const [base, setBase] = useState(project);
  const [stale, setStale] = useState<TaskProject | null>(null);
  const [name, setName] = useState(project?.name ?? '');
  const [targetDate, setTargetDate] = useState(project?.targetDate ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = { name: useId(), target: useId(), description: useId() };
  const submit = async () => {
    if (name.trim() === '' || busy) return;
    setBusy(true);
    setError(null);
    setStale(null);
    const body = { name: name.trim(), targetDate: targetDate === '' ? null : targetDate, description: description.trim() === '' ? null : description };
    try {
      const saved = base
        ? await apiFetch<TaskProject>(getToken, `/api/v1/tasks/projects/${base.id}`, { method: 'PATCH', body: { ...body, version: base.version }, idempotencyKey: newIdempotencyKey() })
        : await apiFetch<TaskProject>(getToken, '/api/v1/tasks/projects', { method: 'POST', body, idempotencyKey: newIdempotencyKey() });
      onSaved(saved, base === undefined);
    } catch (err) {
      if (base && err instanceof ApiProblem && err.code === 'task_project_stale') {
        try {
          const fresh = await apiFetch<TaskProject>(getToken, `/api/v1/tasks/projects/${base.id}`);
          setBase(fresh);
          setStale(fresh);
          void queryClient.invalidateQueries({ queryKey: taskProjectsKey });
        } catch (loadErr) {
          setError(problemText(loadErr));
        }
      } else {
        setError(problemText(err));
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet label={project ? TASK_PROJECT_UI_TEXTS.dialogEdit : TASK_PROJECT_UI_TEXTS.dialogNew} opener={opener} onClose={() => (busy ? undefined : onClose())} className="tablet:mx-auto tablet:max-w-xl">
      <form
        data-testid="projekt-blatt"
        className="flex flex-col gap-space-4 pt-space-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label htmlFor={ids.name} className="flex flex-col gap-space-2">
          <span className="text-body text-ink-2">{TASK_PROJECT_UI_TEXTS.name}</span>
          <Input
            id={ids.name}
            value={name}
            maxLength={TASK_PROJECT_NAME_MAX_LENGTH}
            placeholder={TASK_PROJECT_UI_TEXTS.namePlaceholder}
            onChange={(e) => setName(e.target.value)}
            className="h-11 w-full px-space-4 max-tablet:text-field-phone"
            data-testid="projekt-name-feld"
          />
        </label>
        <div className="flex flex-col gap-space-2">
          <label htmlFor={ids.target} className="text-body text-ink-2">
            {TASK_PROJECT_UI_TEXTS.targetDate}
          </label>
          <span className="flex flex-wrap items-center gap-x-space-3 gap-y-space-1">
            <DateField id={ids.target} value={targetDate} onChange={setTargetDate} today={today} />
            {targetDate !== '' ? (
              <button type="button" onClick={() => setTargetDate('')} className="cursor-pointer text-body text-link underline-offset-2 hover:underline pointer-coarse:min-h-11">
                {TASK_PROJECT_UI_TEXTS.targetClear}
              </button>
            ) : null}
          </span>
        </div>
        <label htmlFor={ids.description} className="flex flex-col gap-space-2">
          <span className="text-body text-ink-2">{TASK_PROJECT_UI_TEXTS.description}</span>
          <Textarea
            id={ids.description}
            autoGrow
            value={description}
            maxLength={TASK_PROJECT_DESCRIPTION_MAX_LENGTH}
            placeholder={TASK_PROJECT_UI_TEXTS.descriptionPlaceholder}
            onChange={(e) => setDescription(e.target.value)}
            className="max-tablet:text-field-phone"
            data-testid="projekt-beschreibung-feld"
          />
        </label>
        {stale ? (
          <div role="alert" className="flex flex-col items-start gap-space-1 text-body text-ink-2" data-testid="projekt-veraltet">
            <p className="m-0">{TASK_PROJECT_UI_TEXTS.staleLoaded(stale)}</p>
            <button
              type="button"
              onClick={() => {
                setName(stale.name);
                setTargetDate(stale.targetDate ?? '');
                setDescription(stale.description ?? '');
                setStale(null);
              }}
              className="cursor-pointer text-link underline-offset-2 hover:underline pointer-coarse:min-h-11"
            >
              {TASK_PROJECT_UI_TEXTS.staleTake}
            </button>
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="m-0 text-body text-clarify">
            {error}
          </p>
        ) : null}
        <div className={cn('flex justify-end', SHEET_ACTIONS)}>
          <Button type="submit" disabled={busy || name.trim() === ''} className="max-tablet:w-full" data-testid="projekt-speichern">
            {project ? TASK_PROJECT_UI_TEXTS.save : TASK_PROJECT_UI_TEXTS.create}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

const NO_PROJECT = '';

/**
 * Feld „Projekt“ im Detail-Blatt (Auswahl mit Suche): speichert sofort (`POST /tasks/{id}/project`) mit 10 s Rückgängig.
 * Wählbar sind aktive Projekte und „Ohne Projekt“; ein abgeschlossenes bleibt als Anzeige stehen.
 */
export function ProjectChooser({
  taskId,
  project,
  disabled,
  offerUndo,
  onSaved,
  id,
}: {
  taskId: string;
  project: { id: string; name: string } | null;
  disabled?: boolean;
  offerUndo: (o: UndoOffer) => void;
  onSaved: (project: { id: string; name: string } | null) => Promise<void>;
  id?: string;
}) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const projects = useTaskProjects();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = [
    { value: NO_PROJECT, label: TASK_PROJECT_TEXTS.noProject },
    ...(projects.data?.items ?? [])
      .filter((p) => p.state === 'active')
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }))
      .map((p) => ({ value: p.id, label: p.name })),
  ];
  const assign = async (projectId: string | null) => {
    const r = await apiFetch<{ project: { id: string; name: string } | null }>(getToken, `/api/v1/tasks/${taskId}/project`, {
      method: 'POST',
      body: { projectId },
      idempotencyKey: newIdempotencyKey(),
    });
    await onSaved(r.project);
    await queryClient.invalidateQueries({ queryKey: taskProjectsKey });
    return r.project;
  };
  const choose = async (value: string | null) => {
    const next = value === null || value === NO_PROJECT ? null : value;
    if (next === (project?.id ?? null) || busy) return;
    const before = project;
    setBusy(true);
    setError(null);
    try {
      const saved = await assign(next);
      const text = saved ? TASK_PROJECT_UI_TEXTS.projectSet(saved.name) : TASK_PROJECT_UI_TEXTS.projectCleared;
      // Zurück in ein abgeschlossenes Projekt lehnt der Server ab (409 task_project_closed): dann nur Bestätigung.
      const beforeClosed = before !== null && projects.data?.items.find((p) => p.id === before.id)?.state !== 'active';
      offerUndo(beforeClosed ? { text } : { text, undo: async () => void (await assign(before?.id ?? null)) });
    } catch (err) {
      setError(problemText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="flex min-w-0 flex-col gap-space-1" data-testid="aufgabe-projekt">
      <Combobox
        {...(id ? { id } : {})}
        label={TASK_PROJECT_UI_TEXTS.project}
        value={project?.id ?? NO_PROJECT}
        options={options}
        selectedLabel={project?.name ?? TASK_PROJECT_TEXTS.noProject}
        loading={projects.isPending}
        disabled={disabled || busy}
        appearance="cell"
        onChange={(value) => void choose(value)}
      />
      {error ? (
        <span role="alert" className="text-body text-clarify">
          {error}
        </span>
      ) : null}
    </span>
  );
}

/** Kleiner Hinweis im Blatt „Neue Aufgabe“: gehört zum Projekt (von der Projektseite). */
export function ProjectNote({ name }: { name: string }): ReactNode {
  return <span className="text-row text-ink">{TASK_PROJECT_UI_TEXTS.inProject(name)}</span>;
}
